import React, { useState, useCallback, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';
import BookingHeader from './Booking/BookingHeader';
import CriticalCSS from '@/components/CriticalCSS';
import { usePriceCalculator } from './Booking/usePriceCalculator';
import { useNavigate } from 'react-router-dom';

// Lazy load non-critical components
const EstimateForm = lazy(() => import('./Booking/EstimateForm'));
const PriceEstimate = lazy(() => import('./Booking/PriceEstimate'));

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const FreeEstimate = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const [formState, setFormState] = useState({
    tripType: 'One Way',
    passengers: 1,
    vehicle: 'Luxury SUV',
    date: new Date(),
    time: '12:00',
    hours: 3,
    pickup: '',
    dropoff: '',
    instructions: '',
    selectedPickup: null,
    selectedDropoff: null,
  });

  const { price, details, distance } = usePriceCalculator(formState);

  const handleFormChange = useCallback((field, value) => {
    setFormState(prevState => ({ ...prevState, [field]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const { pickup, dropoff, tripType, passengers, date, time, vehicle } = formState;
    const isLocationRequired = tripType === 'One Way' || tripType === 'Wait & Return';

    if (isLocationRequired && (!pickup || !dropoff)) {
      toast({ variant: "destructive", title: "Missing Information", description: "Please select valid pickup and drop-off locations from the suggestions." });
      setIsSubmitting(false);
      return;
    }

    // Redirect to booking with prefilled data via navigation state to preserve exact text
    navigate('/booking', { 
      state: { 
        pickup, 
        dropoff, 
        passengers, 
        date: date.toISOString(), 
        time, 
        vehicle,
        tripType 
      } 
    });

    setIsSubmitting(false);
  };

  return (
    <>
      <Helmet>
        <title>Free Estimate - Akiblackcar</title>
        <meta name="description" content="Get your free, no-obligation estimate for luxury transportation with Akiblackcar. No upfront payment or personal data required." />
      </Helmet>
      <CriticalCSS />
      <motion.div initial="hidden" animate="visible" variants={fadeIn} className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <div className="booking-header">
          <BookingHeader />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          <Suspense fallback={
            <div className="flex items-center justify-center p-8" role="status" aria-live="polite" data-testid="estimateform-loading">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-gold"></div>
            </div>
          }>
            <EstimateForm 
              formState={formState}
              onFormChange={handleFormChange}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          </Suspense>
          <Suspense fallback={
            <div className="flex items-center justify-center p-8" role="status" aria-live="polite" data-testid="priceestimate-loading">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-gold"></div>
            </div>
          }>
            <PriceEstimate 
              price={price}
              details={details}
              distance={distance}
              hours={formState.hours}
              tripType={formState.tripType}
            />
          </Suspense>
        </div>
      </motion.div>
    </>
  );
};

export default FreeEstimate;
